import * as React from "react";
import * as HoverCardPrimitive from "@radix-ui/react-hover-card";

import { cn } from "@src/lib/utils/cn";
import Text from "@src/lib/ui/Text";
import { Button, type ButtonProps } from "@src/lib/ui/Button";
import { Route } from "nextjs-routes";

const HoverCard = HoverCardPrimitive.Root;

/**
 * The floating surface HoverCard shares with Popover: elevated, rounded-xl,
 * hairline border, large shadow. Kept in step with PopoverContent by hand.
 */
export const HOVER_CARD_SURFACE_CLASSNAME =
  "rounded-xl border border-border-primary bg-bg-elevated p-4 text-text-primary shadow-large outline-none";

// Fade + slight zoom + a 2px slide away from the trigger, from the side and
// alignment the card opens on (Popover's default motion).
const HOVER_CARD_MOTION_CLASSNAME =
  "origin-[var(--radix-hover-card-content-transform-origin)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 motion-reduce:data-[state=open]:animate-none motion-reduce:data-[state=closed]:animate-none";

const HoverCardTrigger = HoverCardPrimitive.Trigger;

const HoverCardContent = React.forwardRef<
  React.ElementRef<typeof HoverCardPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof HoverCardPrimitive.Content>
>(({ className, align = "center", sideOffset = 4, ...props }, ref) => (
  <HoverCardPrimitive.Portal>
    <HoverCardPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      className={cn(
        "z-50 w-64",
        HOVER_CARD_SURFACE_CLASSNAME,
        HOVER_CARD_MOTION_CLASSNAME,
        className,
      )}
      {...props}
    />
  </HoverCardPrimitive.Portal>
));
HoverCardContent.displayName = HoverCardPrimitive.Content.displayName;

const HoverCardHeading = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex w-full items-center gap-3", className)}
    {...props}
  />
));
HoverCardHeading.displayName = "HoverCardHeading";

const HoverCardTitleSection = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex min-w-0 flex-1 flex-col", className)}
    {...props}
  />
));
HoverCardTitleSection.displayName = "HoverCardTitleSection";

const HoverCardTitle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { children: React.ReactNode }
>(({ className, children, ...props }, ref) => (
  <div ref={ref} {...props}>
    <Text
      variant="body-title"
      multiline
      className={cn("text-text-primary", className)}
    >
      {children}
    </Text>
  </div>
));
HoverCardTitle.displayName = "HoverCardTitle";

const HoverCardSubtitle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { children: React.ReactNode }
>(({ className, children, ...props }, ref) => (
  <div ref={ref} {...props}>
    <Text
      variant="fine-print-description"
      className={cn("text-text-tertiary", className)}
    >
      {children}
    </Text>
  </div>
));
HoverCardSubtitle.displayName = "HoverCardSubtitle";

const HoverCardDetails = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex w-full flex-col gap-2", className)}
    {...props}
  />
));
HoverCardDetails.displayName = "HoverCardDetails";

const HoverCardDetailItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex w-full items-center gap-1.5", className)}
    {...props}
  />
));
HoverCardDetailItem.displayName = "HoverCardDetailItem";

const HoverCardDetailItemIcon = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    icon: React.FC<{ className?: string }>;
    iconClassName?: string;
  }
>(({ className, icon: Icon, iconClassName, ...props }, ref) => (
  <div ref={ref} className={cn("shrink-0", className)} {...props}>
    <Icon className={cn("size-4 text-icon-tertiary", iconClassName)} />
  </div>
));
HoverCardDetailItemIcon.displayName = "HoverCardDetailItemIcon";

type HoverCardDetailItemLabelLink = {
  label: string;
  route: Route;
};

type HoverCardDetailItemLabelProps = React.HTMLAttributes<HTMLDivElement> & {
  children?: React.ReactNode;
  links?: HoverCardDetailItemLabelLink[];
  fontWeight?: "Regular" | "Medium";
};

const HoverCardDetailItemLabel = React.forwardRef<
  HTMLDivElement,
  HoverCardDetailItemLabelProps
>(({ className, children, links, fontWeight = "Medium", ...props }, ref) => {
  if (links && links.length > 0) {
    return (
      <div ref={ref} className={cn("flex flex-wrap", className)} {...props}>
        {links.map((link, index) => (
          <React.Fragment key={link.label}>
            <Text
              variant="fine-print-title"
              link={link.route}
              linkOpenInNewTab
              className={className}
              style={{ textDecoration: "underline" }}
            >
              {link.label}
            </Text>
            {index !== links.length - 1 && (
              <Text variant="fine-print-description" className="mr-0.5">
                ,
              </Text>
            )}
          </React.Fragment>
        ))}
      </div>
    );
  }

  return (
    <div ref={ref} {...props}>
      <Text
        variant="fine-print-description"
        multiline
        className={cn(
          "text-text-secondary",
          fontWeight === "Medium" && "font-medium-v4",
          className,
        )}
      >
        {children}
      </Text>
    </div>
  );
});
HoverCardDetailItemLabel.displayName = "HoverCardDetailItemLabel";

const HoverCardDescription = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { children: React.ReactNode }
>(({ className, children, ...props }, ref) => (
  <div ref={ref} {...props}>
    <Text
      variant="fine-print-description"
      multiline
      className={cn("text-text-secondary", className)}
    >
      {children}
    </Text>
  </div>
));
HoverCardDescription.displayName = "HoverCardDescription";

const HoverCardButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, ...props }, ref) => (
    <Button
      ref={ref}
      variant="outline"
      className={cn("w-full", className)}
      {...props}
    />
  ),
);
HoverCardButton.displayName = "HoverCardButton";

export {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
  HoverCardHeading,
  HoverCardTitleSection,
  HoverCardTitle,
  HoverCardSubtitle,
  HoverCardDetails,
  HoverCardDetailItem,
  HoverCardDetailItemIcon,
  HoverCardDetailItemLabel,
  HoverCardDescription,
  HoverCardButton,
};
